import { MissionDeadline, type DeadlineStatus } from './MissionDeadline';
import { MissionProblems, type ProblemList } from './MissionProblems';
import { missionSelection, switchMissionSelection } from '../host/mission-selection';
import { useDesk } from '../desk/context';
import { useDeskApps } from '../lib/queries';
import { findManifest } from '../host/registry';
import { appDestination } from '../desk/app-destination';
import { AttachedDocument } from '../documents/AttachedDocument';
import type { ClassDocument } from '../lib/queries';
import { MissionResponse } from './MissionResponse';
import { MissionPreviewResponse } from '../teacher/MissionPreviewResponse';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RichText } from '@ndrc/ui';
import { callCore } from '../lib/rpc';
import { t } from '../messages';

interface Progress {
  call?: {
    id: string;
    status: string;
    name: string;
    destination: { appId: string; route: string };
  } | null;
  problems?: ProblemList;
  team?: {
    memberCount: number;
    confirmedCount: number;
    leaderId: string;
    leaderName: string | null;
  } | null;
  version: number;
  title: string;
  activityStatus: string;
  status: string;
  incident: string | null;
  lastDeadline?: { blockId: string; behavior: string } | null;
  intervention: { kind: string; reason: string } | null;
  completedCount: number;
  totalCount: number;
  pendingEffects: boolean;
  resources: ClassDocument[];
  block: {
    id: string;
    completion: string;
    responseKind?: string | null;
    responseOptions?: {
      kind: 'ack' | 'submit' | 'self_declared' | 'teacher';
      remainingSeconds: number;
    }[];
    remainingSeconds?: number | null;
    deadline?: DeadlineStatus | null;
    groupCompletion?: string;
    title: string;
    ui: { text: string; resourceId?: string; app?: string; openRoute?: string };
    document: ClassDocument | null;
    criteria: string[];
    myResponse: string | null;
  } | null;
}
function MissionTool({
  appId,
  route,
  runId,
  title,
}: {
  appId: string;
  route?: string;
  runId: string;
  title: string;
}) {
  const { me, classId, openApp } = useDesk();
  const [error, setError] = useState(false);
  const apps = useDeskApps(classId);
  const manifest = findManifest(appId);
  const available =
    !!manifest &&
    apps.data?.appIds.includes(appId) &&
    (route === undefined || !!appDestination(appId, route));
  return available ? (
    <>
      <button
        className="ndrc-button"
        type="button"
        onClick={() => {
          if (missionSelection()?.runId === runId) {
            openApp(appId, route);
            return;
          }
          try {
            switchMissionSelection(
              me.id,
              { runId, title },
              appDestination(appId, route ?? '/') ?? '/',
            );
          } catch {
            setError(true);
          }
        }}
      >
        {t('activity.open', { name: manifest.name })}
      </button>
      {error && <p role="alert">{t('mission.workspace.storageError')}</p>}
    </>
  ) : (
    <p>{t('activity.openLocked', { name: manifest?.name ?? appId })}</p>
  );
}
export function MissionDetail({
  runId,
  previewClassId,
}: {
  runId: string;
  previewClassId?: string;
}) {
  const { me } = useDesk();
  const [switchError, setSwitchError] = useState(false);
  const query = useQuery({
    queryKey: [
      'core',
      previewClassId ? 'missions.previewProgress' : 'missions.progress',
      runId,
      previewClassId,
    ],
    queryFn: () =>
      callCore<Progress>(previewClassId ? 'missions.previewProgress' : 'missions.progress', {
        runId,
        ...(previewClassId ? { classId: previewClassId } : {}),
      }),
    retry: false,
    refetchInterval: 5000,
  });
  if (query.isPending) return <p role="status">{t('mission.student.loading')}</p>;
  if (query.isError) return <p role="alert">{t('mission.student.error')}</p>;
  const progress = query.data;
  const responseKind = progress.block?.responseKind ?? progress.block?.completion;
  const responseOptions = progress.block?.responseOptions ?? [
    { kind: responseKind, remainingSeconds: progress.block?.remainingSeconds ?? 0 },
  ];
  return (
    <section>
      <h3>{progress.title}</h3>
      {progress.status !== 'reset' && missionSelection()?.runId !== runId && (
        <>
          <p>{t('mission.workspace.hint')}</p>
          <button
            className="ndrc-button"
            type="button"
            onClick={() => {
              try {
                switchMissionSelection(me.id, { runId, title: progress.title });
              } catch {
                setSwitchError(true);
              }
            }}
          >
            {t('mission.workspace.open')}
          </button>
          {switchError && <p role="alert">{t('mission.workspace.storageError')}</p>}
        </>
      )}
      <p>
        {t('mission.student.progress', {
          done: progress.completedCount,
          total: progress.totalCount,
        })}
      </p>
      {progress.activityStatus === 'paused' && <p>{t('mission.student.paused')}</p>}
      {progress.status === 'reset' && <p>{t('mission.student.reset')}</p>}
      {progress.status === 'completed' && (
        <p>
          {t(
            progress.intervention?.kind === 'teacherEnd'
              ? 'mission.student.endedByTeacher'
              : 'mission.student.completed',
          )}
        </p>
      )}
      {progress.lastDeadline && (
        <p>{t('mission.deadline.history', { block: progress.lastDeadline.blockId })}</p>
      )}
      {progress.intervention && <p>{progress.intervention.reason}</p>}
      {progress.incident && <p role="alert">{progress.incident}</p>}
      {progress.pendingEffects && <p role="status">{t('mission.student.pending')}</p>}
      {progress.resources?.map((document) => (
        <AttachedDocument key={document.id} document={document} />
      ))}
      {progress.call && (
        <aside>
          <h4>{t('mission.call.title', { name: progress.call.name })}</h4>
          <p>{t(progress.call.status === 'open' ? 'mission.call.open' : 'mission.call.ended')}</p>
          <MissionTool
            appId={progress.call.destination.appId}
            route={progress.call.destination.route}
            runId={runId}
            title={progress.title}
          />
        </aside>
      )}
      <MissionProblems
        problems={progress.problems}
        previewClassId={previewClassId}
        runId={runId}
        enabled={progress.activityStatus === 'running' && progress.status !== 'reset'}
        onResolved={
          previewClassId
            ? undefined
            : async () => {
                await query.refetch();
              }
        }
      />
      {progress.block && (
        <>
          <h4>{progress.block.title}</h4>
          {progress.team && progress.block.groupCompletion === 'all_members' && (
            <p>
              {t('mission.workspace.teamConfirmations', {
                done: progress.team.confirmedCount,
                total: progress.team.memberCount,
              })}
            </p>
          )}
          {progress.team && progress.block.groupCompletion === 'leader' && (
            <p>
              {t('mission.workspace.teamLeader', {
                name: progress.team.leaderName ?? t('mission.workspace.leaderUnavailable'),
              })}
            </p>
          )}

          <MissionDeadline value={progress.block.deadline} />
          {progress.block.remainingSeconds != null && progress.block.remainingSeconds > 0 && (
            <p role="status">
              {t('mission.student.timerRemaining', { seconds: progress.block.remainingSeconds })}
            </p>
          )}
          {progress.block.remainingSeconds === 0 && responseKind === 'ack' && (
            <p>{t('mission.student.timerReady')}</p>
          )}
          <RichText text={progress.block.ui.text} />
          {progress.block.ui.app && (
            <MissionTool
              appId={progress.block.ui.app}
              route={progress.block.ui.openRoute}
              runId={runId}
              title={progress.title}
            />
          )}
          {progress.block.document && <AttachedDocument document={progress.block.document} />}
          {progress.block.ui.resourceId && !progress.block.document && (
            <p role="alert">{t('mission.resource.unavailable')}</p>
          )}
          {progress.block.criteria.length > 0 && (
            <>
              <h4>{t('mission.student.criteria')}</h4>
              <ul>
                {progress.block.criteria.map((criterion, index) => (
                  <li key={index}>{criterion}</li>
                ))}
              </ul>
            </>
          )}
          {progress.block.myResponse && (
            <>
              <h4>{t('mission.student.response')}</h4>
              <p>{progress.block.myResponse}</p>
            </>
          )}
        </>
      )}
      {progress.block &&
        progress.status !== 'completed' &&
        responseOptions
          .filter((option) =>
            ['ack', 'submit', 'self_declared', ...(previewClassId ? ['teacher'] : [])].includes(
              option.kind ?? '',
            ),
          )
          .map((option) => (
            <div key={`${progress.block!.id}:${option.kind}`}>
              {responseOptions.length > 1 && option.remainingSeconds > 0 && (
                <p role="status">
                  {t('mission.student.timerRemaining', { seconds: option.remainingSeconds })}
                </p>
              )}
              {previewClassId ? (
                <MissionPreviewResponse
                  classId={previewClassId}
                  runId={runId}
                  blockId={progress.block!.id}
                  version={progress.version}
                  kind={
                    option.kind === 'teacher'
                      ? 'teacherValidate'
                      : (option.kind as 'ack' | 'submit' | 'self_declared')
                  }
                  isolatedChoice={responseOptions.length > 1}
                  enabled={
                    progress.activityStatus === 'running' &&
                    option.remainingSeconds === 0 &&
                    !progress.pendingEffects &&
                    !progress.incident &&
                    progress.status !== 'reset'
                  }
                />
              ) : (
                <MissionResponse
                  runId={runId}
                  blockId={progress.block!.id}
                  version={progress.version}
                  kind={option.kind as 'ack' | 'submit' | 'self_declared'}
                  isolatedChoice={responseOptions.length > 1}
                  enabled={
                    progress.activityStatus === 'running' &&
                    option.remainingSeconds === 0 &&
                    !progress.pendingEffects &&
                    !progress.incident &&
                    progress.status !== 'reset'
                  }
                />
              )}
            </div>
          ))}
    </section>
  );
}
export function MissionBoard({ classId }: { classId: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['core', 'missions.mine', classId],
    queryFn: () =>
      callCore<{ runs: { runId: string; title: string }[] }>('missions.mine', { classId }),
    retry: false,
    refetchInterval: 5000,
  });
  if (query.isPending || (!query.isError && query.data.runs.length === 0)) return null;
  return (
    <section className="teacher-card">
      <h2>{t('mission.student.title')}</h2>
      <button
        type="button"
        className="ndrc-button"
        onClick={() => {
          void query.refetch();
        }}
      >
        {t('mission.student.refresh')}
      </button>
      {query.isError ? (
        <p role="alert">{t('mission.student.error')}</p>
      ) : (
        <>
          <ul>
            {query.data.runs.map((run) => (
              <li key={run.runId}>
                <button
                  type="button"
                  className="ndrc-button"
                  aria-pressed={selected === run.runId}
                  onClick={() => setSelected(run.runId)}
                >
                  {run.title}
                </button>
              </li>
            ))}
          </ul>
          {selected && query.data.runs.some((run) => run.runId === selected) && (
            <MissionDetail key={selected} runId={selected} />
          )}
        </>
      )}
    </section>
  );
}
